/**
 * Role Formatting Utility (Data-driven)
 * 
 * Formats role keys into human-readable labels.
 * Uses a cache populated from the sport-roles API, with a smart fallback
 * for any keys not yet cached.
 * 
 * Usage:
 *   import { formatRole, loadRolesForSport } from '../utils/roleFormatting';
 *   
 *   // On page/component load, preload roles from the API:
 *   await loadRolesForSport('Soccer');
 *   
 *   // Then format any role key:
 *   formatRole('full_back')  // => 'Full Back' (from API cache or smart fallback)
 */

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Cache: roleKey -> roleName (populated from API)
let roleLabelsMap = {};

// Track which sports have been loaded
const loadedSports = new Set();

/**
 * Load roles for a sport from the database and populate the cache.
 * Safe to call multiple times — skips if already loaded.
 */
export const loadRolesForSport = async (sport, forceRefresh = false) => {
  if (!sport) return;

  const key = sport.toLowerCase();
  if (!forceRefresh && loadedSports.has(key)) return;

  try {
    const [playerRes, coachRes] = await Promise.allSettled([
      fetch(`${API_BASE_URL}/api/sport-roles/sport/${sport}/player`).then(r => r.json()),
      fetch(`${API_BASE_URL}/api/sport-roles/sport/${sport}/coach`).then(r => r.json()),
    ]);

    const processRoles = (result) => {
      if (result.status === 'fulfilled' && result.value?.success && Array.isArray(result.value.roles)) {
        result.value.roles.forEach((role) => {
          if (role.roleKey && role.roleName) {
            roleLabelsMap[role.roleKey] = role.roleName;
            roleLabelsMap[role.roleKey.toLowerCase()] = role.roleName;
          }
        });
      }
    };

    processRoles(playerRes);
    processRoles(coachRes);
    loadedSports.add(key);
  } catch (error) {
    console.log(`Could not load sport roles for ${sport}`);
  }
};

/**
 * Format a role key to a human-readable display name.
 * 
 * Priority:
 *  1. Check the API cache (data-driven from database)
 *  2. Smart fallback: convert snake_case/kebab-case/camelCase to Title Case
 */
export const formatRole = (roleKey) => {
  if (!roleKey) return 'Player';

  // 1. Check exact match in API cache, then lowercase
  if (roleLabelsMap[roleKey]) return roleLabelsMap[roleKey];
  if (roleLabelsMap[roleKey.toLowerCase()]) return roleLabelsMap[roleKey.toLowerCase()];

  // 2. Smart fallback: convert snake_case/kebab-case/camelCase to Title Case
  return roleKey
    .replace(/[-_]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 * Clear the role cache (useful for testing or when switching contexts)
 */
export const clearRoleCache = () => {
  roleLabelsMap = {};
  loadedSports.clear();
};

export default { formatRole, loadRolesForSport, clearRoleCache };
